import { useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { useTranslation } from "react-i18next"
import { api, ApiRequestError } from "@/lib/api"
import { cn } from "@/lib/utils"
import { CountrySelect } from "../CountrySelect"

interface GuestUbyportEditProps {
    reservationId: string
    guestIndex: number
    initialNationality: string
    initialDocumentNumber: string
    initialPurposeOfStay: string
    queryKey: unknown[]
    onDone: () => void
}

// Inline form to correct a guest's Ubyport fields (nationality / document /
// purpose) after the Foreign Police rejected the record — typically error 105
// (invalid nationality). Saving resets the guest's Ubyport status server-side,
// so the City Report shows it as pending and the admin can resend.
export const GuestUbyportEdit = ({
    reservationId,
    guestIndex,
    initialNationality,
    initialDocumentNumber,
    initialPurposeOfStay,
    queryKey,
    onDone,
}: GuestUbyportEditProps) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()

    const [nationality, setNationality] = useState(initialNationality.toUpperCase())
    const [documentNumber, setDocumentNumber] = useState(initialDocumentNumber)
    const [purposeOfStay, setPurposeOfStay] = useState(initialPurposeOfStay)

    const valid =
        nationality.length === 2 && documentNumber.trim().length >= 4 && purposeOfStay.trim().length >= 1

    const save = useMutation({
        mutationFn: () =>
            api.patch(`/reservations/${reservationId}/guests/${guestIndex}/ubyport-fields`, {
                nationality,
                documentNumber: documentNumber.trim(),
                purposeOfStay: purposeOfStay.trim(),
            }),
        onSuccess: () => {
            toast.success(t("admin.detail.ubyportEdit.saved"))
            queryClient.invalidateQueries({ queryKey })
            onDone()
        },
        onError: (e) => {
            toast.error(e instanceof ApiRequestError ? e.message : t("admin.detail.ubyportEdit.saveFailed"))
        },
    })

    const labelClass = "text-xs font-black uppercase tracking-widest text-slate-500 mb-1 block"
    const inputClass =
        "h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-base font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"

    return (
        <div className="mt-2 space-y-3 rounded-xl border border-slate-200 bg-white p-3">
            <div>
                <label className={labelClass}>{t("admin.detail.ubyportEdit.nationality")}</label>
                <CountrySelect value={nationality} onChange={setNationality} />
            </div>
            <div>
                <label className={labelClass}>{t("admin.detail.ubyportEdit.documentNumber")}</label>
                <input
                    value={documentNumber}
                    onChange={(e) => setDocumentNumber(e.target.value)}
                    className={inputClass}
                    maxLength={30}
                />
            </div>
            <div>
                <label className={labelClass}>{t("admin.detail.ubyportEdit.purposeOfStay")}</label>
                <input
                    value={purposeOfStay}
                    onChange={(e) => setPurposeOfStay(e.target.value)}
                    className={inputClass}
                    maxLength={128}
                />
            </div>
            <p className="text-xs font-bold text-slate-400">{t("admin.detail.ubyportEdit.resendHint")}</p>
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    onClick={() => save.mutate()}
                    disabled={!valid || save.isPending}
                    className={cn(
                        "flex-1 rounded-xl bg-brand px-4 py-2.5 text-sm font-black text-white",
                        "hover:bg-brand-dark active:scale-95 disabled:opacity-50 disabled:active:scale-100",
                    )}
                >
                    {save.isPending ? t("admin.detail.ubyportEdit.saving") : t("admin.detail.ubyportEdit.save")}
                </button>
                <button
                    type="button"
                    onClick={onDone}
                    disabled={save.isPending}
                    className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-black text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                >
                    {t("admin.detail.ubyportEdit.cancel")}
                </button>
            </div>
        </div>
    )
}
